/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

/* Modified version of https://github.com/adobe/react-spectrum */
/* Inspired by https://github.com/AlmeroSteyn/react-aria-live */
const LIVEREGION_TIMEOUT_DELAY = 7000;

let liveAnnouncer = null;

/**
 * Announces the message using screen reader technology.
 */
export function announce(
  message,
  timeout = LIVEREGION_TIMEOUT_DELAY,
) {
  if (!liveAnnouncer) {
    liveAnnouncer = new LiveAnnouncer();
  }

  liveAnnouncer.announce(message, timeout);
}

/**
 * Stops all queued announcements.
 */
export function clearAnnouncer() {
  if (liveAnnouncer) {
    liveAnnouncer.clear();
  }
}

/**
 * Removes the announcer from the DOM.
 */
export function destroyAnnouncer() {
  if (liveAnnouncer) {
    liveAnnouncer.destroy();
    liveAnnouncer = null;
  }
}

// LiveAnnouncer is implemented using vanilla DOM, not React. That's because as of React 18
// ReactDOM.render is deprecated, and the replacement, ReactDOM.createRoot is moved into a
// subpath import `react-dom/client`. That makes it hard for us to support multiple React versions.
// As a global API, we can't use portals without introducing a breaking API change. LiveAnnouncer
// is simple enough to implement without React, so that's what we do here.
// See this discussion for more details: https://github.com/reactwg/react-18/discussions/125#discussioncomment-2382638
class LiveAnnouncer {
  node;

  constructor() {
    this.node = this.findExistingLiveRegion('polite');

    if (!this.node) {
      this.node = this.createLog('polite');
      document.body.prepend(this.node);
    }
  }

  findExistingLiveRegion(ariaLive) {
    let node = document.getElementById('live-region');
    if (!node) return null;

    node.setAttribute('role', 'log');
    node.setAttribute('aria-live', ariaLive);
    node.setAttribute('aria-relevant', 'additions');
    return node;
  }

  createLog(ariaLive) {
    let node = document.createElement('div');
    node.id = 'live-region';
    node.classList.add('assistText');
    node.setAttribute('role', 'log');
    node.setAttribute('aria-live', ariaLive);
    node.setAttribute('aria-relevant', 'additions');
    return node;
  }

  destroy() {
    this.node.remove();
    this.node = null;
  }

  announce(message, timeout) {
    let node = document.createElement('div');
    node.textContent = message;

    this.node.appendChild(node);

    if (message !== '') {
      setTimeout(() => {
        node.remove();
      }, timeout);
    }
  }

  clear() {
    this.node.innerHTML = '';
  }
}
